import lodash from 'lodash';
import type { FunctionComponent } from 'react';
import React from 'react';

import type { WixRicosViewerProps } from './../wix-ricos-common';
import {
  experimentStrategy,
  extractPluginsConfig,
  fetchEmbedData,
  runStrategies,
  themeStrategy,
} from './../wix-ricos-common';
import { RICOS_VERSION } from './../ricos-schema/next-schema';
import type { RicosViewerProps } from './../ricos-viewer-next/types';
import { useGetWixClientPromise } from './../_shared/request-service-provider/use-get-wix-client-promise';

import { runViewerStrategies } from './runViewerStrategies';

export const createWixRicosViewer = (
  RicosViewer: React.ComponentType<RicosViewerProps>
): FunctionComponent<WixRicosViewerProps> =>
  function WixRicosViewer(props: WixRicosViewerProps) {
    const {
      children,
      biSettings,
      instance,
      plugins,
      theme,
      wixExperiments,
      allowLinkPreviewPopover,
      msid,
      baseUrl,
      previewMode,
      content,
      ...rest
    } = props;

    const experiments = experimentStrategy(wixExperiments);
    const { getWixClientPromise } = useGetWixClientPromise(instance);

    const strategyProps = runStrategies({
      plugins,
      instance,
      biSettings,
      experiments,
      wixRicosVersion: RICOS_VERSION,
      ricosVersion: RICOS_VERSION,
      getWixClientPromise,
      msid,
      viewer: true,
      content,
    });
    const _rcProps: WixRicosViewerProps['_rcProps'] = lodash.merge(
      strategyProps,
      runViewerStrategies(plugins, baseUrl, previewMode),
      extractPluginsConfig(plugins, strategyProps.helpers)
    );

    return (
      <RicosViewer
        _rcProps={_rcProps}
        baseUrl={baseUrl}
        content={content}
        experiments={strategyProps.experiments}
        plugins={plugins}
        theme={themeStrategy(theme)}
        {...(allowLinkPreviewPopover && {
          linkPreviewPopoverFetchData: fetchEmbedData(instance),
        })}
        {...rest}
      >
        {children}
      </RicosViewer>
    );
  };
